/** Executive KPI card with inline mini chart.
 *
 *  v3 (Feb 2026 redesign):
 *    - CartesianGrid at #F3F4F6 provides a subtle baseline reference.
 *    - X-axis labels are horizontal (no rotation) and shortened to "Tue 1"
 *      via a tickFormatter. First / last are always kept; middle labels are
 *      thinned so they never clip on narrow cards.
 *    - Cross-card hover synchronisation via Recharts' native ``syncId`` —
 *      hovering one card highlights the same bucket on the other two and
 *      opens a synchronised tooltip on all three.
 *    - `breakdownDenomSeries` — per-bucket denominator (e.g. daily net sales)
 *      used to render a "% of sales" indicator next to each Food/Bev split
 *      row in the hover tooltip.
 */
import {
  ResponsiveContainer, ComposedChart, Bar, Cell, Line, Tooltip,
  YAxis, XAxis, CartesianGrid, ReferenceLine, ReferenceArea, LabelList,
} from "recharts";
import { TrendUp, TrendDown, Minus } from "@phosphor-icons/react";
import { ExtremeTag, ExtremeBadge, extremeCellProps } from "@/components/extremes";
import { weekGroups, WeekLine, WeekLabel } from "@/components/weeks";
import { useTooltipDismiss } from "@/lib/useTooltipDismiss";

function fmtMoneyCompact(v) {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1000) return `£${(n / 1000).toFixed(1)}k`;
  return `£${n.toFixed(0)}`;
}

function fmtMoneyFull(v, isPercent) {
  if (isPercent) return `${(Number(v) || 0).toFixed(1)}%`;
  return `£${Number(v || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
}

/** "Tue 01 Sep" -> "Tue 1"; leaves other labels untouched. */
function shortLabel(l) {
  if (!l || typeof l !== "string") return l;
  const parts = l.split(" ");
  if (parts.length >= 2 && /^\d+$/.test(parts[1])) {
    return `${parts[0]} ${parseInt(parts[1], 10)}`;
  }
  return l;
}

/** Bucket tooltip: shows the hovered day's bar value plus a Food/Beverage
 *  breakdown enriched with a "% of sales" indicator (using the parent-provided
 *  denominator series — typically net_sales per day). Also renders the running
 *  cumulative total and STLY comparison when enabled.
 */
function KpiTooltip({
  active, payload, label, isPercent,
  breakdownIndex, breakdownSeries, breakdownDenomSeries, stlyPerBucket, accent,
}) {
  if (!active || !payload?.length) return null;
  const valuePayload = payload.find((p) => p.dataKey === "value");
  const cumPayload = payload.find((p) => p.dataKey === "cumulative");
  const bar = Number(valuePayload?.value ?? 0);
  const idx = breakdownIndex;

  const stlyTip = stlyPerBucket != null ? (() => {
    const delta = bar - stlyPerBucket;
    const pct = stlyPerBucket ? (delta / Math.abs(stlyPerBucket)) * 100 : 0;
    const sign = pct > 0 ? "+" : "";
    return { pct: `${sign}${pct.toFixed(1)}%`, delta: `${sign}${fmtMoneyCompact(delta)}` };
  })() : null;

  // "% of sales" denominator for the hovered day — falls back to bar value
  // so, for the Net Sales card, the Food/Bev pct is Food/day-sales (i.e. the
  // classic 55/45 split), and for the Purchases card, Food purchases as a
  // percentage of that day's net sales.
  const denom = breakdownDenomSeries && idx != null
    ? Number(breakdownDenomSeries[idx]?.value ?? 0)
    : bar;

  return (
    <div className="bg-white border border-[#E2E8F0] shadow-lg rounded-md p-2.5 sm:p-3 text-[11px] min-w-[180px] sm:min-w-[220px]" data-testid="kpi-tooltip">
      <div className="mb-2 border-b border-[#EEEEEE] pb-1.5">
        <p className="font-medium text-[#0F172A]">{label}</p>
        <ExtremeBadge extreme={valuePayload?.payload?.extreme} />
      </div>
      <div className="flex items-center justify-between gap-3 mb-1">
        <span className="flex items-center gap-1.5 text-[#0F172A] font-medium">
          <span className="inline-block w-2 h-2 rounded-sm" style={{ background: accent }} />
          {valuePayload?.name === "cumulative" ? "Cumulative" : "Value"}
        </span>
        <span className="font-mono-num font-semibold text-[#0F172A]">{fmtMoneyFull(bar, isPercent)}</span>
      </div>
      {breakdownSeries && idx != null && Object.entries(breakdownSeries).map(([key, series]) => {
        const v = Number(series?.[idx]?.value ?? 0);
        if (!v) return null;
        const pct = denom > 0 ? (v / denom) * 100 : null;
        return (
          <div key={key} className="flex items-baseline justify-between gap-3 text-[11px]" data-testid={`kpi-tooltip-${key.toLowerCase()}`}>
            <span className="text-[#94A3B8]">· {key}</span>
            <span className="font-mono-num text-[#475569]">
              {fmtMoneyFull(v, false)}
              {pct != null && <span className="text-[#94A3B8] ml-2">{pct.toFixed(1)}%</span>}
            </span>
          </div>
        );
      })}
      {(cumPayload || (!isPercent && valuePayload?.payload?.week != null)) && (
        <div className="mt-1.5 pt-1.5 border-t border-[#EEEEEE] space-y-1">
          {!isPercent && <WeekLine d={valuePayload?.payload} fmt={(v) => fmtMoneyFull(v, false)} />}
          {cumPayload && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-[#475569]">Running total</span>
              <span className="font-mono-num text-[#0F172A]">{fmtMoneyFull(cumPayload.value, isPercent)}</span>
            </div>
          )}
        </div>
      )}
      {stlyTip && (
        <div className="flex items-center justify-between gap-3 mt-1.5 pt-1.5 border-t border-[#EEEEEE]">
          <span className="text-[#475569]">vs STLY</span>
          <span className="font-mono-num text-[#475569]">{stlyTip.pct} · {stlyTip.delta}</span>
        </div>
      )}
    </div>
  );
}

export default function KpiCard({
  label,
  value,
  subValue,
  series = [],
  accent = "#0F172A",
  testid,
  isPercent = false,
  stly,
  stlyUnavailable = false,
  footer,
  editor,
  showCumulative = false,
  showBudget = false,
  stlyInTooltip = null,
  breakdownSeries = null,
  breakdownDenomSeries = null,       // per-bucket denominator series (e.g. net sales) for "% of sales" in the tooltip
  chartType = "bar",                 // "bar" | "line"
  onBarClick = null,                 // (bucket) => void — invoked when a bar/point is clicked
  invertDelta = false,               // true for costs: an increase is bad (red)
}) {
  const display = isPercent ? `${(value ?? 0).toFixed(1)}%` : value;
  const hasSpark = series && series.length > 0;

  let chartData = hasSpark ? series : [];
  if (hasSpark && showCumulative) {
    let run = 0;
    chartData = series.map((d) => {
      run += Number(d.value) || 0;
      return { ...d, cumulative: run };
    });
  }
  if (hasSpark) chartData = chartData.map((d, i) => ({ ...d, _idx: i }));
  const todayLabel = chartData.find((d) => d.isToday)?.label;
  const todayNearEnd = chartData.findIndex((d) => d.isToday) > chartData.length * 0.7;
  const tip = useTooltipDismiss();
  const hasExtremes = chartData.some((d) => d.extreme);
  const weeks = isPercent ? [] : weekGroups(chartData);

  // Thin x-axis ticks so labels never overlap on narrow cards. Recharts sadly
  // has no built-in "N ticks" mode with tickFormatter — we compute the
  // interval based on the series length so ~6 ticks are visible.
  const tickInterval = chartData.length > 12
    ? Math.max(1, Math.round(chartData.length / 6) - 1)
    : 0;

  const stlyPerBucket = stlyInTooltip && hasSpark
    ? (Number(stlyInTooltip.value) || 0) / series.length
    : null;

  let stlyDelta = null;  if (stly && typeof stly.value === "number" && stly.value !== 0) {
    const numericCurrent = typeof value === "number" ? value : parseFloat(String(value).replace(/[^0-9.\-]/g, ""));
    const delta = numericCurrent - stly.value;
    const pct = (delta / Math.abs(stly.value)) * 100;
    stlyDelta = { delta, pct, direction: pct > 0.5 ? "up" : pct < -0.5 ? "down" : "flat" };
  }

  return (
    <div
      data-testid={testid}
      className="bg-white border border-[#E2E8F0] rounded-lg p-4 sm:p-6 flex flex-col gap-3 card-fade"
    >
      {/* Top row — sub-label + right meta aligned on a shared baseline */}
      <div className="flex items-baseline justify-between gap-2 h-4">
        <div className="flex items-center gap-1.5">
          <p className="uppercase text-[11px] tracking-[0.12em] text-[#475569] font-semibold">
            {label}
          </p>
          {editor}
        </div>
        {subValue && (
          <p className="text-[11px] font-medium text-[#475569] uppercase tracking-[0.14em]" data-testid={`${testid}-subvalue`}>
            {subValue}
          </p>
        )}
      </div>

      {/* Primary value */}
      <p
        className="font-mono-num tracking-tight text-[28px] sm:text-[32px] leading-none font-bold"
        style={{ color: accent }}
        data-testid={`${testid}-value`}
      >
        {display}
      </p>

      {/* STLY row */}
      {stlyDelta && (
        <div className="flex items-center gap-2" data-testid={`${testid}-stly`}>
          <p className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-mono-num font-semibold ${
              stlyDelta.direction === "flat" ? "bg-[#EEEEEE] text-[#475569]"
                : (stlyDelta.direction === "up") !== invertDelta ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
            }`} data-testid={`${testid}-delta-badge`}>
            {stlyDelta.direction === "up" && <TrendUp size={11} weight="bold" />}
            {stlyDelta.direction === "down" && <TrendDown size={11} weight="bold" />}
            {stlyDelta.direction === "flat" && <Minus size={11} weight="bold" />}
            {stlyDelta.pct > 0 ? "+" : ""}{stlyDelta.pct.toFixed(1)}% vs STLY
          </p>
          <span className="text-[11px] text-[#94A3B8] font-mono">· {fmtMoneyCompact(stly.value)}</span>
        </div>
      )}
      {!stlyDelta && stlyUnavailable && (
        <div data-testid={`${testid}-stly-unavailable`}>
          <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-[#94A3B8]">
            STLY n/a · Xero rate-limited
          </p>
        </div>
      )}

      {footer}

      {hasSpark && (
        <div className="mt-auto pt-2" data-testid={`${testid}-spark`}>
          <div className={`${weeks.length ? "h-36" : "h-28"} -mx-1`} {...tip.wake}>
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 1, height: 1 }}>
              <ComposedChart
                data={chartData}
                barCategoryGap={3}
                margin={{ top: weeks.length ? 48 : hasExtremes ? 16 : 8, right: 10, bottom: 4, left: 10 }}
              >
                <CartesianGrid vertical={false} stroke="#F3F4F6" strokeDasharray="0" />
                <YAxis yAxisId="bars" hide domain={[0, "auto"]} />
                {showCumulative && (
                  <YAxis yAxisId="cum" orientation="right" hide domain={[0, "auto"]} />
                )}
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 9, fill: "#475569", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}
                  tickLine={false}
                  axisLine={{ stroke: "#E2E8F0" }}
                  interval={tickInterval}
                  height={18}
                  tickFormatter={shortLabel}
                  padding={{ left: 6, right: 6 }}
                />
                <Tooltip
                  active={tip.active}
                  allowEscapeViewBox={{ x: false, y: true }}
                  wrapperStyle={{ zIndex: 30, maxWidth: "min(260px, 78vw)" }}
                  cursor={{ fill: "rgba(28,25,23,0.06)" }}
                  content={(p) => (
                    <KpiTooltip
                      {...p}
                      isPercent={isPercent}
                      breakdownIndex={p?.payload?.[0]?.payload?._idx}
                      breakdownSeries={breakdownSeries}
                      breakdownDenomSeries={breakdownDenomSeries}
                      stlyPerBucket={stlyPerBucket}
                      accent={accent}
                    />
                  )}
                />
                {chartType === "line" ? (
                  <Line
                    yAxisId="bars"
                    type="monotone"
                    dataKey="value"
                    stroke={accent}
                    strokeWidth={2.25}
                    dot={{ r: 2.5, fill: accent, strokeWidth: 0 }}
                    activeDot={{ r: 4, fill: accent, strokeWidth: 2, stroke: "#fff" }}
                    isAnimationActive={false}
                  />
                ) : (
                  <Bar
                    yAxisId="bars"
                    dataKey="value"
                    radius={[3, 3, 0, 0]}
                    cursor={onBarClick ? "pointer" : undefined}
                    onClick={onBarClick ? (d) => onBarClick(d?.payload || d) : undefined}
                  >
                    {chartData.map((d) => (
                      <Cell key={d.label} fill={accent} fillOpacity={d.isCurrent ? 1 : 0.55} {...extremeCellProps(d)} />
                    ))}
                    {hasExtremes && <LabelList dataKey="extreme" content={(p) => <ExtremeTag {...p} />} />}
                  </Bar>
                )}
                {weeks.map((w, i) => (
                  <ReferenceArea
                    key={w.week}
                    yAxisId="bars"
                    x1={w.x1}
                    x2={w.x2}
                    fill={i % 2 ? "#EEEEEE" : "#FFFFFF"}
                    fillOpacity={i % 2 ? 0.7 : 0}
                    stroke="#E2E8F0"
                    strokeOpacity={i > 0 ? 1 : 0}
                    strokeDasharray="2 3"
                    ifOverflow="visible"
                    label={(p) => <WeekLabel {...p} week={w.week} total={w.total} fontSize={11} />}
                  />
                ))}
                {todayLabel && (
                  <ReferenceLine
                    yAxisId="bars"
                    x={todayLabel}
                    stroke="#64748B"
                    strokeDasharray="3 3"
                    strokeWidth={1}
                    label={{ value: "Today", position: todayNearEnd ? "insideTopRight" : "insideTopLeft", fontSize: 9, fill: "#475569", fontFamily: "ui-monospace, monospace" }}
                    data-testid={`${testid}-today-line`}
                  />
                )}
                {showCumulative && (
                  <Line
                    yAxisId="cum"
                    type="monotone"
                    dataKey="cumulative"
                    stroke={accent}
                    strokeWidth={2}
                    strokeOpacity={0.85}
                    dot={false}
                    isAnimationActive={false}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          {showBudget && (
            <p className="text-[11px] font-mono text-[#94A3B8] uppercase tracking-[0.18em] mt-1.5" data-testid={`${testid}-budget-stub`}>
              Budget overlay — coming soon
            </p>
          )}
        </div>
      )}
    </div>
  );
}
